// Loads Shaka Player on demand instead of bundling it into main.js.
//
// Only the player screen uses Shaka, so bundling it made every other screen pay
// for it — and 2.5.23 is 320 KB, three times the 1.6.5 build the figure behind
// this decision was measured against. A dynamic import() puts it in its own
// webpack chunk, fetched the first time a movie is opened.
//
// Verified on real Cobalt: the chunk loads and the API is present, in 24 ms on
// 11 (the 1.6.5 chunk, on boxes now retired) and 25 ms on 22 (2.5.23, the chunk
// that ships today). Cobalt loads webpack's chunks the same way it loads any
// injected script, which was the open question. Neither number predicts 19: the
// only timing for the shipping chunk came from a box with V8 and JIT, and 19 has
// neither.
//
// One build ships to the whole fleet, shaka-player 2.5.23. There used to be two,
// because the fleet used to be two: 1.6.5 for the Cobalt 11 boxes and 2.5.23 for
// Cobalt 22. Qarva updated the HiSilicon boxes from 11 to 19 (2026-08-20) and
// confirmed 2.5.x runs there, so the 1.x path is gone (CP-2353).
//
// 2.5.23 is not a preference, it is the ceiling: measured on a real Cobalt 22
// box, 3.3.19 cannot resolve a manifest parser, 4.16.42 never resolves attach(),
// and 5.2.2 needs a URL constructor Cobalt does not have.
/**
 * Cobalt's major version, or -1 in a normal browser. Cobalt puts it in the user
 * agent as "Cobalt/<major>.x" — there is no API for it.
 */
export var getCobaltMajor = function (userAgent) {
    if (userAgent === void 0) { userAgent = navigator.userAgent || ""; }
    var match = userAgent.match(/Cobalt\/(\d+)/);
    if (!match) {
        return -1;
    }
    return parseInt(match[1], 10);
};
/**
 * What the box reports on window, verbatim. Not always a bare version: some
 * boxes prefix it with a chipset or vendor name. Empty on boxes that predate
 * the attribute and in a desktop browser.
 */
export var getStvVersion = function () {
    try {
        return window.STVVersion ? String(window.STVVersion) : "";
    }
    catch (_a) {
        return "";
    }
};
/**
 * Just the version number out of the above, or "" when the field is absent or
 * holds no number at all.
 *
 * Takes the LAST number in the string, not the first: a chipset prefix carries
 * numbers of its own, dotted ones included ("Broadcom 7251.2 19.1.4"), and the
 * version is what trails. A bare major with no dots ("22") still counts — that
 * number is the one thing the loading screen is there to show.
 *
 * `matchAll` would read better and is ES2020; SpiderMonkey 45 does not have it.
 */
export var getStvVersionNumber = function () {
    var numbers = getStvVersion().match(/[0-9]+(?:[.][0-9]+)*/g);
    return numbers ? numbers[numbers.length - 1] : "";
};
var pending;
var loaded;
/**
 * Resolves once Shaka is on the page with its polyfills installed. Safe to call
 * repeatedly — the load happens once per session and every later call gets the
 * same promise.
 */
export var loadShaka = function () {
    if (loaded) {
        return Promise.resolve(loaded);
    }
    if (pending) {
        return pending;
    }
    // The specifier is a string literal on purpose. webpack builds its chunk list
    // by reading import() calls statically, so a computed specifier would emit no
    // chunk at all and fail at runtime.
    pending = import("shaka-player").then(function (module) {
        var shaka = (module.default ||
            module);
        // Runs once per session, since the cached promise means one load. Doing it
        // here rather than where the player is built keeps it off the episode and
        // quality-change paths.
        shaka.polyfill.installAll();
        loaded = shaka;
        return loaded;
    });
    pending.catch(function () {
        pending = undefined;
    });
    return pending;
};
/**
 * Shaka, for code that already knows it is loaded. Throws rather than returning
 * undefined so a mistake surfaces where it happens instead of as a confusing
 * property access on nothing.
 */
export var getShaka = function () {
    if (!loaded) {
        throw new Error("Shaka is not loaded — await loadShaka() first");
    }
    return loaded;
};



//////////////////
// WEBPACK FOOTER
// ./src/utils/LoadShaka.ts
// module id = 27
// module chunks = 1